import { useState, useRef, useCallback } from 'react';
import api from './api';

// 站内通知收件箱的状态（配合 components/PushInbox.js 使用）：
//   · list：还没处理的通知，按时间（id）从早到晚
//   · 当前位置用通知 id 记，不用下标：新通知进来、关掉某条后位置不会错位
//   · addPushes：按 id 去重（socket 实时推送和进入页面时的补拉可能是同一条）；
//     第一次有内容时停在最新一条；弹层已经打开时不打断正在看的这条，只把 newCount +1
//   · closeOne：关当前这条，显示下一条（没有下一条就显示前一条），这条标记已读
//   · closeAll：一键全部关闭，只把“给用户看过的这些条”一次标记已读（不误伤关闭瞬间刚到的新通知）
export default function usePushInbox() {
  const [list, setList] = useState([]);
  const [curId, setCurId] = useState(null);
  const [newCount, setNewCount] = useState(0);
  const listRef = useRef([]);
  const tmpSeq = useRef(0);

  const addPushes = useCallback((arr) => {
    const prev = listRef.current;
    const seen = new Set(prev.map(x => x.id));
    const fresh = [];
    for (const raw of arr || []) {
      if (!raw) continue;
      const item = raw.id != null ? raw : { ...raw, id: Number.MAX_SAFE_INTEGER - (++tmpSeq.current) };   // 没有 id 的兜底，排在最后
      if (seen.has(item.id)) continue;
      seen.add(item.id); fresh.push(item);
    }
    if (!fresh.length) return;
    const next = [...prev, ...fresh].sort((a, b) => a.id - b.id);
    listRef.current = next;
    setList(next);
    if (prev.length === 0) { setCurId(next[next.length - 1].id); setNewCount(0); }
    else setNewCount(n => n + fresh.length);
  }, []);

  const realIds = (items) => items.map(x => x.id).filter(id => Number.isInteger(id) && id > 0 && id < 1e12);
  const index = (() => { const i = list.findIndex(x => x.id === curId); return i >= 0 ? i : Math.max(0, list.length - 1); })();

  const goTo = useCallback((i) => {
    const l = listRef.current;
    if (!l[i]) return;
    setCurId(l[i].id);
    if (i === l.length - 1) setNewCount(0);
  }, []);

  const closeOne = useCallback(() => {
    const l = listRef.current;
    const i = l.findIndex(x => x.id === curId);
    if (i < 0) return;
    const gone = l[i];
    const next = l.filter((_, k) => k !== i);
    listRef.current = next;
    setList(next);
    setCurId(next.length ? (next[i] || next[i - 1]).id : null);
    if (!next.length) setNewCount(0);
    const ids = realIds([gone]);
    if (ids.length) api.post('/notifications/mine/read', { ids }).catch(() => {});
  }, [curId]);

  const closeAll = useCallback(() => {
    const l = listRef.current;
    listRef.current = [];
    setList([]); setCurId(null); setNewCount(0);
    const ids = realIds(l);
    if (ids.length) api.post('/notifications/mine/read', { ids }).catch(() => {});
  }, []);

  return { list, index, newCount, addPushes, goTo, closeOne, closeAll };
}
